// SA-7 — KPI-дашборд (фаза 4)

import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_MACHINE_EVENTS,
  COL_MACHINES,
  COL_PRODUCTION_REQUEST_LINES,
  COL_SCHEDULE_SLOTS,
} from '../../config/collections'
import { Alert, PageHeader, PageShell } from '../../components/ui'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import { ShiftFilterSelect } from '../../components/ShiftFilterSelect'
import { formInputProps } from '../../components/ui/FormField'
import type { CsvColumn } from '../../lib/exportCsv'
import { useAssignedShiftFilter } from '../../hooks/useAssignedShiftFilter'
import { downtimeByReason } from '../../lib/machineEvents'
import { hasMaterialDeficit, aggregateMaterialRequirements, loadBomByProductIds } from '../../lib/materialRequirements'
import {
  averageOutputDeviationPercent,
  machineUtilizationPercent,
} from '../../lib/oeeCalculations'
import { fetchCalendarMap } from '../../lib/loadProductionCalendar'
import { countPlantWorkingDaysInRange } from '../../lib/productionCalendar'
import { pb } from '../../pocketbase'
import type { MachineEventRecord, MachineRecord, ProductionRequestLineRecord, ScheduleSlotRecord } from '../../types/records'
import { btn, cn, labelClass, tableCellClass, tableClass, tableHeadClass, tableLastRowClass, tableRowHoverClass, tableWrapClass, toolbarClass } from '../../lib/ui'

type KpiSnapshot = {
  machineLoadPct: number
  onTimePct: number
  backlogLines: number
  backlogQty: number
  avgDeviationPct: number | null
  materialDeficit: boolean
  downtimeRows: { label: string; hours: number }[]
}

export function KpiPage() {
  const { shiftId, setShiftId, shifts, canAllShifts, shiftLocked, appendShiftFilter } =
    useAssignedShiftFilter()
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return d.toISOString().slice(0, 10)
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [slots, setSlots] = useState<ScheduleSlotRecord[]>([])
  const [lines, setLines] = useState<ProductionRequestLineRecord[]>([])
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [events, setEvents] = useState<MachineEventRecord[]>([])
  const [materialDeficit, setMaterialDeficit] = useState(false)
  const [calendarMap, setCalendarMap] = useState(() => new Map<string, { isWorking: boolean }>())

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    const fromIso = `${fromDate} 00:00:00.000Z`
    const toIso = `${toDate} 23:59:59.999Z`
    try {
      const [slotList, lineList, machineList, eventList, calMap] = await Promise.all([
        pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          filter: appendShiftFilter(
            `plannedStart >= "${fromIso}" && plannedStart <= "${toIso}" && status != "cancelled"`,
          ),
          expand: 'requestLine.product',
          sort: 'plannedStart',
        }),
        pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
          filter: 'status = "pending" || status = "scheduled" || status = "in_progress"',
        }),
        pb.collection(COL_MACHINES).getFullList<MachineRecord>({ sort: 'sort' }),
        pb.collection(COL_MACHINE_EVENTS).getFullList<MachineEventRecord>({
          filter: `startAt >= "${fromIso}" && startAt <= "${toIso}"`,
        }),
        fetchCalendarMap(fromDate, toDate),
      ])
      setSlots(slotList)
      setLines(lineList)
      setMachines(machineList)
      setEvents(eventList)
      setCalendarMap(calMap)

      const productIds = [
        ...new Set(
          slotList
            .map((s) => s.expand?.requestLine?.product)
            .filter((id): id is string => typeof id === 'string'),
        ),
      ]
      const bomMap = await loadBomByProductIds(productIds)
      const mrp = aggregateMaterialRequirements(slotList, bomMap)
      setMaterialDeficit(hasMaterialDeficit(mrp))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки KPI'))
    } finally {
      setLoading(false)
    }
  }, [fromDate, toDate, appendShiftFilter])

  useEffect(() => {
    void load()
  }, [load])

  const kpi = useMemo((): KpiSnapshot => {
    const fromIso = `${fromDate} 00:00:00.000Z`
    const toIso = `${toDate} 23:59:59.999Z`
    const days = Math.max(1, countPlantWorkingDaysInRange(fromDate, toDate, calendarMap))

    const availableHours = machines.reduce((sum, m) => sum + (Number(m.working_hours_per_day) || 0) * days, 0)
    const machineLoadPct = machineUtilizationPercent(slots, availableHours)

    const withDeadline = lines.filter((l) => l.plannedProduction)
    const onTimeCount = withDeadline.filter((l) => {
      if (!l.plannedProduction) return false
      if (l.status === 'completed' || l.status === 'shipped') {
        const actual = l.actualShip ? new Date(l.actualShip) : new Date()
        return actual.getTime() <= new Date(l.plannedProduction).getTime()
      }
      return new Date() <= new Date(l.plannedProduction)
    }).length
    const onTimePct =
      withDeadline.length > 0 ? Math.round((onTimeCount / withDeadline.length) * 1000) / 10 : 100

    const backlogLines = lines.length
    const backlogQty = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0)
    const avgDeviationPct = averageOutputDeviationPercent(slots)

    const downtimeRows = downtimeByReason(events, fromIso, toIso).map((r) => ({
      label: r.label,
      hours: r.hours,
    }))

    return {
      machineLoadPct,
      onTimePct,
      backlogLines,
      backlogQty,
      avgDeviationPct,
      materialDeficit,
      downtimeRows,
    }
  }, [slots, lines, machines, events, fromDate, toDate, materialDeficit, calendarMap])

  const kpiCsvRows = useMemo(
    () => [
      { metric: 'Загрузка станков', value: `${kpi.machineLoadPct}%` },
      { metric: 'Заявки в срок', value: `${kpi.onTimePct}%` },
      { metric: 'Backlog (строк)', value: String(kpi.backlogLines) },
      { metric: 'Backlog (шт.)', value: String(kpi.backlogQty) },
      {
        metric: 'Ср. отклонение выработки',
        value: kpi.avgDeviationPct != null ? `${kpi.avgDeviationPct}%` : '—',
      },
      { metric: 'Дефицит материалов', value: kpi.materialDeficit ? 'Есть' : 'Нет' },
      ...kpi.downtimeRows.map((r) => ({ metric: `Простой: ${r.label}`, value: `${r.hours} ч` })),
    ],
    [kpi],
  )

  const kpiCsvColumns: CsvColumn<{ metric: string; value: string }>[] = useMemo(
    () => [
      { header: 'Показатель', value: (r) => r.metric },
      { header: 'Значение', value: (r) => r.value },
    ],
    [],
  )

  return (
    <PageShell>
      <PageHeader
        title="KPI производства"
        lead="Сводные показатели за период: загрузка станков, сроки заявок, backlog, отклонение выработки и простои."
      />
      {err && <Alert kind="err">{err}</Alert>}

      <div className={toolbarClass}>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>С</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} {...formInputProps('max-w-40')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>По</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} {...formInputProps('max-w-40')} />
        </label>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <ShiftFilterSelect
          shiftId={shiftId}
          shifts={shifts}
          canAllShifts={canAllShifts}
          locked={shiftLocked}
          onChange={setShiftId}
        />
        {!loading && (
          <ExportCsvButton filename={`kpi-${fromDate}-${toDate}`} rows={kpiCsvRows} columns={kpiCsvColumns} />
        )}
      </div>

      {loading ? (
        <p className="text-sm text-su-text">
          <em>Загрузка…</em>
        </p>
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard title="Загрузка станков" value={`${kpi.machineLoadPct}%`} hint="Слоты / доступное время" />
            <KpiCard title="Заявки в срок" value={`${kpi.onTimePct}%`} hint="По plannedProduction" />
            <KpiCard title="Backlog" value={String(kpi.backlogLines)} hint={`${kpi.backlogQty.toLocaleString('ru-RU')} шт.`} />
            <KpiCard
              title="Ср. отклонение выработки"
              value={kpi.avgDeviationPct != null ? `${kpi.avgDeviationPct > 0 ? '+' : ''}${kpi.avgDeviationPct}%` : '—'}
              hint="Факт vs план по слотам"
            />
            <KpiCard
              title="Дефицит материалов"
              value={kpi.materialDeficit ? 'Есть' : 'Нет'}
              hint="По графику и MRP"
            />
          </div>

          <h3 className="mb-2 text-base font-semibold text-su-text-strong">Топ простоев по причинам</h3>
          <div className={cn(tableWrapClass, tableLastRowClass, 'max-w-xl')}>
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={tableHeadClass}>Причина</th>
                  <th className={tableHeadClass}>Часы</th>
                </tr>
              </thead>
              <tbody>
                {kpi.downtimeRows.length === 0 && (
                  <tr className={tableRowHoverClass}>
                    <td className={tableCellClass} colSpan={2}>
                      <em>Нет простоев за период (загрузите события станков)</em>
                    </td>
                  </tr>
                )}
                {kpi.downtimeRows.map((row) => (
                  <tr key={row.label} className={tableRowHoverClass}>
                    <td className={tableCellClass}>{row.label}</td>
                    <td className={tableCellClass}>{row.hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageShell>
  )
}

function KpiCard({ title, value, hint }: { title: string; value: string; hint: string }) {
  return (
    <div className="rounded-su-lg border border-su-border bg-su-surface p-4 shadow-su-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-su-text">{title}</div>
      <div className="mt-1 text-2xl font-bold text-su-text-strong">{value}</div>
      <div className="mt-1 text-xs text-su-text">{hint}</div>
    </div>
  )
}
